import type { DayKey, GameEvent, QuestProgress } from "./types";

/**
 * Quests/missions: objectives with a period (daily/weekly/once). An objective
 * accumulates by a METRIC (event type or goal). The quest completes when all
 * of its goals reach their `target`. Pure.
 */

export type QuestPeriod = "daily" | "weekly" | "once";

export interface QuestGoal {
  /** Metric key: normally the event's `type`. */
  metric: string;
  target: number;
  /** How much each event adds: 1 (count) or the event's `value`. */
  useValue?: boolean;
}

export interface QuestDef {
  id: string;
  title: string;
  period: QuestPeriod;
  goals: QuestGoal[];
  bonusXp?: number;
}

/** Period key for reset (daily = day; weekly = ISO Monday; once = fixed). */
export function periodKey(period: QuestPeriod, day: DayKey): string {
  if (period === "once") {
    return "once";
  }
  if (period === "daily") {
    return day;
  }
  // Weekly: anchors to ISO Monday.
  const d = new Date(`${day}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // lunes=0
  d.setUTCDate(d.getUTCDate() - dow);
  return `w:${d.toISOString().slice(0, 10)}`;
}

function emptyProgress(pk: string): QuestProgress {
  return { progress: {}, completed: false, periodKey: pk };
}

/**
 * Applies an event to a quest and returns the new progress (does not mutate).
 * If the period changed with respect to the previous progress, resets before
 * applying.
 */
export function applyEventToQuest(
  def: QuestDef,
  prev: QuestProgress | undefined,
  event: GameEvent,
  day: DayKey,
): QuestProgress {
  const pk = periodKey(def.period, day);
  const base: QuestProgress =
    prev && prev.periodKey === pk ? { ...prev, progress: { ...prev.progress } } : emptyProgress(pk);

  // Once completed stays completed.
  if (def.period === "once" && prev?.completed) {
    return prev;
  }

  for (const goal of def.goals) {
    if (goal.metric === event.type) {
      const inc = goal.useValue ? (event.value ?? 0) : 1;
      base.progress[goal.metric] = (base.progress[goal.metric] ?? 0) + inc;
    }
  }
  base.completed = def.goals.every((g) => (base.progress[g.metric] ?? 0) >= g.target);
  return base;
}
